import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from 'react';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'soft'
  | 'outline'
  | 'outline-danger'
  | 'danger'
  | 'success'
  | 'ghost'
  | 'ghost-primary'
  | 'ghost-danger'
  | 'ghost-warning'
  | 'ghost-primary-hover'
  | 'ghost-danger-hover'
  | 'ghost-inverse';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

interface ButtonBaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Remix Icon のクラス名（例: 'ri-add-line'）。指定すると先頭にアイコンを表示。 */
  icon?: string;
  /** 丸型（rounded-full）にする。リアクション等のチップに使用。 */
  pill?: boolean;
  /** アイコンのみの正方形ボタンにする（テキストを併記しない操作ボタン用）。 */
  iconOnly?: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * href を渡すと <a>（リンク）として、省略すると <button> として描画される。
 * どちらでも variant / size / iconOnly の見た目は完全に共通。
 */
type ButtonProps =
  | (ButtonBaseProps &
      ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined })
  | (ButtonBaseProps &
      AnchorHTMLAttributes<HTMLAnchorElement> & { href: string });

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-[color:var(--theme-primary)] text-white hover:bg-[color:var(--theme-primary-hover)]',
  secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
  soft: 'bg-[color:var(--theme-primary-light)] text-[color:var(--theme-primary)] hover:opacity-85',
  outline: 'border border-gray-300 text-gray-700 hover:bg-gray-50',
  'outline-danger': 'border border-red-600 text-red-600 hover:bg-red-50',
  danger: 'bg-red-500 text-white hover:bg-red-600',
  success: 'bg-green-600 text-white hover:bg-green-700',
  ghost: 'text-gray-600 hover:bg-gray-100',
  // 常に色付きのゴーストアイコン操作（一覧の編集・削除など）
  'ghost-primary':
    'text-[color:var(--theme-primary)] hover:bg-[color:var(--theme-primary-light)]',
  'ghost-danger': 'text-red-500 hover:bg-red-50',
  'ghost-warning': 'text-amber-500 hover:bg-amber-50',
  // 通常はグレー、hover時のみ色付くゴーストアイコン操作
  'ghost-primary-hover':
    'text-gray-600 hover:text-[color:var(--theme-primary)] hover:bg-[color:var(--theme-primary-light)]',
  'ghost-danger-hover': 'text-gray-600 hover:text-red-500 hover:bg-red-50',
  // テーマ色のグラデーション等、濃い背景の上に置く白いゴーストアイコン（ナビ矢印など）
  'ghost-inverse': 'text-white hover:bg-white/20',
};

const sizeClasses: Record<ButtonSize, string> = {
  xs: 'px-2 py-1 text-xs',
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-sm',
};

// アイコンのみボタン用の正方形サイズ
const iconOnlySizeClasses: Record<ButtonSize, string> = {
  xs: 'w-6 h-6 text-sm',
  sm: 'w-8 h-8 text-base',
  md: 'w-9 h-9 text-base',
  lg: 'w-10 h-10 text-lg',
};

/**
 * アプリ共通のボタン。
 * variant で用途（primary / soft / outline / outline-danger / danger / success と ghost 系の色トーン）を切り替え、
 * primary・ghost-primary 系は事業所テーマカラー（--theme-primary）に自動追従する。
 * href を渡すと <a> として描画され、アイコンリンク（名刺のリンク等）も同じ見た目で表現できる。
 */
export default function Button(props: ButtonProps) {
  const {
    variant = 'primary',
    size = 'md',
    icon,
    pill = false,
    iconOnly = false,
    className = '',
    children,
    ...rest
  } = props;

  const sizing = iconOnly ? iconOnlySizeClasses[size] : sizeClasses[size];
  const spacing = iconOnly ? 'p-0' : 'gap-1.5';
  const classes = `${variantClasses[variant]} ${sizing} ${
    pill ? 'rounded-full' : 'rounded-lg'
  } ${spacing} font-medium whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center ${className}`;

  const content = (
    <>
      {icon && <i className={`${icon} ${iconOnly ? '' : 'text-base'}`}></i>}
      {children}
    </>
  );

  const href = (rest as { href?: unknown }).href;

  if (typeof href === 'string') {
    return (
      <a
        href={href}
        className={classes}
        {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      className={classes}
      {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {content}
    </button>
  );
}